import { useEffect, useMemo, useRef, useState } from "react";
import type { Location, Service, Staff, TimeSlot } from "./types/models";
import { VerificationMethod } from "./types/models";
import { listServices } from "./api/service";
import { listStaffForService } from "./api/staff";
import { getAvailability } from "./api/availability";
import { WaitlistForm } from "./WaitlistForm";
import { createReservation } from "./api/reservation";
import { getCompanyInfo } from "./api/company";
import { previewDiscountCode } from "./api/discountCode";
import { getQueryParam } from "./utils/url";
import { STRINGS, localeFor, normalizeLang, type Lang } from "./i18n";
import { FlagGB, FlagNL } from "./flags";

type BookFlowProps = {
    companySlug: string;
    apiBase: string;
    lang?: Lang;
    /** Als meegegeven, is de taal "controlled" door de ouder (Widget.tsx) — nodig zodat
     * de modal-titel buiten BookFlow om ook meeschakelt. Zonder deze prop beheert
     * BookFlow zijn eigen taalstate (bruikbaar als BookFlow los ingezet wordt). */
    onLangChange?: (lang: Lang) => void;
    onDone?: () => void;
    /** Turnstile-token — alleen meegegeven vanaf de eigen /boek/{bedrijf}-pagina
     * (boekkoek.nl); bij een embed op een klant-site blijft dit undefined. */
    captchaToken?: string;
};

type UiState = "idle" | "loadingInit" | "loadingStaff" | "submitting" | "success";
type Step = 1 | 2 | 3 | 4 | 5;
type StepKey = "location" | "service" | "staff" | "dateTime" | "details";

function todayISODate() {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}

function formatPrice(priceCents: number | null | undefined, lang: Lang) {
    if (typeof priceCents !== "number") return null;
    return new Intl.NumberFormat(localeFor(lang), {
        style: "currency",
        currency: "EUR",
    }).format(priceCents / 100);
}

function formatTime(iso: string, lang: Lang) {
    return new Date(iso).toLocaleTimeString(localeFor(lang), {
        hour: "2-digit",
        minute: "2-digit",
    });
}

function formatDateLabel(isoDate: string, lang: Lang) {
    try {
        return new Intl.DateTimeFormat(localeFor(lang), {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
        }).format(new Date(`${isoDate}T12:00:00`));
    } catch {
        return isoDate;
    }
}

function formatMonthLabel(isoDate: string, lang: Lang) {
    try {
        return new Intl.DateTimeFormat(localeFor(lang), {
            month: "long",
            year: "numeric",
        }).format(new Date(`${isoDate}T12:00:00`));
    } catch {
        return isoDate;
    }
}

/** Dienstnaam die de klant zelf typt: toont de Engelse naam in EN-modus, met een
 * automatische terugval op de Nederlandse naam als er geen vertaling is ingevuld. */
function displayServiceName(service: { name: string; nameEn?: string | null } | null | undefined, lang: Lang) {
    if (!service) return "";
    if (lang === "en" && service.nameEn) return service.nameEn;
    return service.name;
}

function getInitials(name?: string | null) {
    if (!name) return "MW";
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() ?? "")
        .join("");
}

function buildCalendarGrid(referenceDate: string) {
    const [year, month] = referenceDate.split("-").map(Number);
    const first = new Date(year, month - 1, 1);
    const weekday = first.getDay();
    const mondayOffset = weekday === 0 ? 6 : weekday - 1;

    const start = new Date(first);
    start.setDate(first.getDate() - mondayOffset);

    const result: Array<{
        iso: string;
        day: number;
        inMonth: boolean;
        isPast: boolean;
    }> = [];

    const today = todayISODate();

    for (let i = 0; i < 35; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);

        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const iso = `${yyyy}-${mm}-${dd}`;

        result.push({
            iso,
            day: d.getDate(),
            inMonth: d.getMonth() === first.getMonth(),
            isPast: iso < today,
        });
    }

    return result;
}

export default function BookFlow({ companySlug, apiBase, lang: langProp, onLangChange, onDone, captchaToken }: BookFlowProps) {
    const [internalLang, setInternalLang] = useState<Lang>(normalizeLang(langProp));
    // "Controlled" zodra de ouder een lang-prop + onLangChange meegeeft (Widget.tsx doet
    // dit altijd) — anders beheert BookFlow zijn eigen taalstate.
    const lang: Lang = onLangChange ? normalizeLang(langProp) : internalLang;
    const changeLang = (next: Lang) => {
        if (onLangChange) onLangChange(next);
        else setInternalLang(next);
    };
    const t = STRINGS[lang];

    const [step, setStep] = useState<Step>(1);
    const [ui, setUi] = useState<UiState>("loadingInit");

    const [services, setServices] = useState<Service[]>([]);
    const [staff, setStaff] = useState<Staff[]>([]);
    const [slots, setSlots] = useState<TimeSlot[]>([]);
    const [locations, setLocations] = useState<Location[]>([]);

    const [locationId, setLocationId] = useState<number | null>(null);
    const [serviceId, setServiceId] = useState<number | null>(null);
    // "all" = alle diensten, null = de impliciete "Overig"-groep (diensten zonder
    // categorie), een getal = die specifieke categorie. Alleen relevant als er
    // daadwerkelijk categorieën in gebruik zijn — zie hasServiceCategories hieronder.
    const [selectedServiceCategory, setSelectedServiceCategory] = useState<number | "all" | null>("all");
    const [serviceSearch, setServiceSearch] = useState("");
    const [staffId, setStaffId] = useState<number | null>(null);
    const [date, setDate] = useState<string>(todayISODate());
    const [slotStart, setSlotStart] = useState<string>("");
    // Losgekoppeld van `date`: welke maand de kalender toont. Bladeren door maanden
    // mag geen nieuwe availability-fetch triggeren (dat gebeurt pas als je echt een
    // dag aanklikt) en mag de al gekozen dag niet resetten.
    const [viewMonth, setViewMonth] = useState<string>(() => todayISODate().slice(0, 7));

    const [customerName, setCustomerName] = useState("");
    const [customerEmail, setCustomerEmail] = useState("");
    const [customerEmailTouched, setCustomerEmailTouched] = useState(false);
    const [customerPhone, setCustomerPhone] = useState("");
    const [notes, setNotes] = useState("");

    const [verificationMethod, setVerificationMethod] =
        useState<(typeof VerificationMethod)[keyof typeof VerificationMethod]>(VerificationMethod.EMAIL);
    const [smsEnabled, setSmsEnabled] = useState(false);
    // Herinnering via WhatsApp — zelfde "uit totdat companyInfo 'm bevestigt"-patroon als
    // hieronder; whatsappOptIn is het vinkje dat de klant zelf zet in de laatste stap.
    const [whatsappEnabled, setWhatsappEnabled] = useState(false);
    const [whatsappOptIn, setWhatsappOptIn] = useState(false);
    // PRO-feature — bepaalt of de NL/EN-taalknop getoond wordt. Standaard uit totdat de
    // companyInfo-fetch hieronder 'm bevestigt, zodat een BASIC-bedrijf nooit even de
    // knop ziet flitsen voordat de fetch terug is.
    const [multiLangEnabled, setMultiLangEnabled] = useState(false);
    // Optionele aanbetaling (Mollie) — zelfde "uit totdat companyInfo 'm bevestigt"-patroon
    // als multiLangEnabled hierboven.
    const [depositEnabled, setDepositEnabled] = useState(false);
    const [bookingsClosed, setBookingsClosed] = useState(false);
    const [waitlistEnabled, setWaitlistEnabled] = useState(false);
    const [depositAmountCents, setDepositAmountCents] = useState(0);

    // Kortingscode uit de URL (?discount=..., bv. vanuit een EmptySlotNudgeJob-mail-link) —
    // eenmalig bij het laden gelezen, nooit meer gewijzigd. discountPercent wordt pas gezet
    // zodra de code succesvol tegen serviceId+staffId is gevalideerd; blijft null (en dus
    // stilzwijgend genegeerd) bij een ongeldige/verlopen/verkeerd-gescoopte code.
    const [discountCodeFromUrl] = useState<string | null>(() => getQueryParam("discount"));
    const [discountPercent, setDiscountPercent] = useState<number | null>(null);

    const [error, setError] = useState<string>("");
    const [successMsg, setSuccessMsg] = useState<string>("");
    // Verhoog om de beschikbaarheid van de huidige dag opnieuw op te halen zonder dat de
    // gebruiker zelf de datum hoeft aan te raken — nodig na een 409 bij het boeken (het
    // gekozen tijdstip bleek tussentijds niet meer beschikbaar).
    const [slotsRefreshToken, setSlotsRefreshToken] = useState(0);
    // Los van `ui` (die blokkeert de hele stap) — snel door dagen klikken mag de kalender
    // niet blokkeren, alleen de tijdenlijst zelf toont een subtiele "ververst"-staat.
    const [slotsLoading, setSlotsLoading] = useState(false);
    // Voorkomt dat een trage fetch voor een oude datum de resultaten van een inmiddels
    // alweer nieuwere datum overschrijft (race condition bij snel doorklikken).
    const slotsRequestIdRef = useRef(0);

    const isValidEmail = useMemo(
        () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail.trim()),
        [customerEmail]
    );
    const showEmailError = customerEmailTouched && customerEmail.trim().length > 0 && !isValidEmail;

    const selectedService = useMemo(
        () => services.find((s) => s.id === serviceId) ?? null,
        [services, serviceId]
    );

    // Categorie-tabs alleen tonen als dit bedrijf ze daadwerkelijk gebruikt — zonder
    // categorieën blijft de dienstenlijst gewoon zoals voorheen (geen lege tabbenbalk).
    const serviceCategoryTabs = useMemo(() => {
        const seen = new Map<number, string>();
        let hasUncategorized = false;

        for (const s of services) {
            if (s.categoryId != null) {
                seen.set(s.categoryId, s.categoryName || t.service.otherCategory);
            } else {
                hasUncategorized = true;
            }
        }

        const tabs: { id: number | null; name: string }[] = Array.from(seen.entries())
            .map(([id, name]) => ({ id: id as number | null, name }))
            .sort((a, b) => a.name.localeCompare(b.name));

        if (hasUncategorized && tabs.length > 0) {
            tabs.push({ id: null, name: t.service.otherCategory });
        }

        return tabs;
    }, [services, t.service.otherCategory]);

    const hasServiceCategories = serviceCategoryTabs.length > 0;

    const visibleServices = useMemo(() => {
        let list = services;

        if (hasServiceCategories && selectedServiceCategory !== "all") {
            list = list.filter((s) => (s.categoryId ?? null) === selectedServiceCategory);
        }

        const query = serviceSearch.trim().toLowerCase();
        if (query) {
            list = list.filter((s) => displayServiceName(s, lang).toLowerCase().includes(query));
        }

        return list;
    }, [services, hasServiceCategories, selectedServiceCategory, serviceSearch, lang]);

    const selectedStaff = useMemo(
        () => staff.find((s) => s.id === staffId) ?? null,
        [staff, staffId]
    );

    const selectedSlot = useMemo(
        () => slots.find((s) => s.start === slotStart) ?? null,
        [slots, slotStart]
    );

    const canLoadSlots = useMemo(() => !!(serviceId && staffId && date), [serviceId, staffId, date]);
    const calendarDays = useMemo(() => buildCalendarGrid(`${viewMonth}-01`), [viewMonth]);
    const isCurrentMonth = viewMonth === todayISODate().slice(0, 7);

    // Bij precies één vestiging (verreweg de meeste bedrijven) slaat de widget de
    // locatiekeuze-stap helemaal over — niemand merkt dat multi-locatie ondersteund wordt.
    const hasMultipleLocations = locations.length > 1;
    const totalSteps: number = hasMultipleLocations ? 5 : 4;
    const stepOrder: StepKey[] = hasMultipleLocations
        ? ["location", "service", "staff", "dateTime", "details"]
        : ["service", "staff", "dateTime", "details"];
    const stepKey = (s: Step): StepKey => stepOrder[s - 1] ?? "service";

    const selectedLocation = useMemo(
        () => locations.find((l) => l.id === locationId) ?? null,
        [locations, locationId]
    );

    function shiftViewMonth(delta: number) {
        const [y, m] = viewMonth.split("-").map(Number);
        const next = new Date(y, m - 1 + delta, 1);
        setViewMonth(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`);
    }

    function resetForm() {
        setStep(1);
        // Bij één vestiging blijft die impliciet gekozen (geen stap om opnieuw te doen);
        // bij meerdere vestigingen moet de klant bij een nieuwe boeking opnieuw kiezen.
        setLocationId(locations.length === 1 ? locations[0].id : null);
        setServiceId(null);
        setSelectedServiceCategory("all");
        setServiceSearch("");
        setStaffId(null);
        setDate(todayISODate());
        setViewMonth(todayISODate().slice(0, 7));
        setSlotStart("");
        setCustomerName("");
        setCustomerEmail("");
        setCustomerPhone("");
        setNotes("");
        setWhatsappOptIn(false);
        setSlots([]);
        setStaff([]);
        setError("");
        setSuccessMsg("");
        setUi("idle");
    }

    useEffect(() => {
        (async () => {
            try {
                setError("");
                setUi("loadingInit");
                const s = await listServices(apiBase, companySlug);
                setServices(s ?? []);
                setStaff([]);
                setUi("idle");
            } catch (e: any) {
                setUi("idle");
                setError(e?.message ?? t.errors.loadServicesFailed);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiBase, companySlug]);

    useEffect(() => {
        (async () => {
            try {
                const info = await getCompanyInfo(apiBase, companySlug);
                setSmsEnabled(!!info.smsEnabled);
                setWhatsappEnabled(!!info.whatsappEnabled);
                setMultiLangEnabled(!!info.multiLangEnabled);
                setDepositEnabled(!!info.depositEnabled);
                setDepositAmountCents(info.depositAmountCents ?? 0);
                setBookingsClosed(info.bookingsEnabled === false);
                setWaitlistEnabled(info.waitlistEnabled === true);

                const locs = info.locations ?? [];
                setLocations(locs);
                // Bij precies één vestiging alvast impliciet kiezen, zodat serviceId/staffId
                // meteen correct kunnen filteren zonder dat de klant iets hoeft te doen.
                if (locs.length === 1) {
                    setLocationId(locs[0].id);
                }
            } catch {
                // Geen harde fout tonen voor deze check — zonder companyInfo valt de
                // widget gewoon terug op alleen email-verificatie en geen locatiekeuze.
                setSmsEnabled(false);
                setWhatsappEnabled(false);
            }
        })();
    }, [apiBase, companySlug]);

    useEffect(() => {
        if (!smsEnabled && verificationMethod === VerificationMethod.PHONE) {
            setVerificationMethod(VerificationMethod.EMAIL);
        }
    }, [smsEnabled, verificationMethod]);

    // Zet het bedrijf WhatsApp uit terwijl het formulier al openstaat, dan mag er geen
    // toestemming blijven hangen die niemand meer kan zien.
    useEffect(() => {
        if (!whatsappEnabled && whatsappOptIn) {
            setWhatsappOptIn(false);
        }
    }, [whatsappEnabled, whatsappOptIn]);

    // Een eerder op deze site opgeslagen EN-keuze (localStorage, zie widget.tsx) mag geen
    // Engels blijven tonen als het bedrijf inmiddels geen PRO meer heeft — val dan terug op NL.
    useEffect(() => {
        if (!multiLangEnabled && lang === "en") {
            changeLang("nl");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [multiLangEnabled]);

    useEffect(() => {
        (async () => {
            if (!serviceId) {
                setStaff([]);
                setStaffId(null);
                return;
            }

            try {
                setError("");
                setUi("loadingStaff");
                setStaffId(null);
                setSlots([]);
                setSlotStart("");

                const result = await listStaffForService(apiBase, companySlug, serviceId, locationId);
                setStaff(result ?? []);
                setUi("idle");
            } catch (e: any) {
                setUi("idle");
                setStaff([]);
                setStaffId(null);
                setError(e?.message ?? t.errors.loadStaffFailed);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiBase, companySlug, serviceId, locationId]);

    useEffect(() => {
        // Selectie hoort niet bij de vorige datum/medewerker te blijven staan — dit is
        // een goedkope class-toggle, geen DOM-heropbouw, dus geen flicker-risico.
        setSlotStart("");
        setError("");

        if (!canLoadSlots) {
            setSlots([]);
            return;
        }

        const requestId = ++slotsRequestIdRef.current;
        let cancelled = false;
        setSlotsLoading(true);

        (async () => {
            try {
                const av = await getAvailability(apiBase, companySlug, staffId!, serviceId!, date);
                // Een tragere fetch voor een datum waar de gebruiker inmiddels alweer
                // vandaan geklikt is mag de nieuwste resultaten niet overschrijven.
                if (cancelled || slotsRequestIdRef.current !== requestId) return;
                setSlots(av ?? []);
            } catch (e: any) {
                if (cancelled || slotsRequestIdRef.current !== requestId) return;
                setSlots([]);
                setError(e?.message ?? t.errors.loadAvailabilityFailed);
            } finally {
                if (!cancelled && slotsRequestIdRef.current === requestId) {
                    setSlotsLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiBase, companySlug, canLoadSlots, serviceId, staffId, date, slotsRefreshToken]);

    useEffect(() => {
        // Nooit een korting van een vorige service/medewerker laten staan terwijl de
        // nieuwe validatie nog loopt.
        setDiscountPercent(null);

        if (!discountCodeFromUrl || !serviceId || !staffId) return;

        let cancelled = false;
        (async () => {
            try {
                const preview = await previewDiscountCode(apiBase, companySlug, discountCodeFromUrl, serviceId, staffId);
                if (!cancelled) setDiscountPercent(preview.discountPercent);
            } catch {
                // Ongeldige/verlopen/verkeerd-gescoopte code — stilzwijgend negeren, dit mag
                // de boekingsflow nooit blokkeren of een foutmelding tonen.
                if (!cancelled) setDiscountPercent(null);
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiBase, companySlug, discountCodeFromUrl, serviceId, staffId]);

    function nextStep() {
        const key = stepKey(step);
        if (key === "location" && !locationId) {
            setError(t.errors.chooseLocationFirst);
            return;
        }
        if (key === "service" && !serviceId) {
            setError(t.errors.chooseServiceFirst);
            return;
        }
        if (key === "staff" && !staffId) {
            setError(t.errors.chooseStaffFirst);
            return;
        }
        if (key === "dateTime" && !slotStart) {
            setError(t.errors.chooseTimeFirst);
            return;
        }

        setError("");
        setStep((prev) => Math.min(totalSteps, prev + 1) as Step);
    }

    function prevStep() {
        setError("");
        setStep((prev) => Math.max(1, prev - 1) as Step);
    }

    async function onSubmit() {
        if (!serviceId || !staffId || !slotStart || !customerName || !customerEmail) {
            setError(t.errors.fillEverything);
            return;
        }

        if (!isValidEmail) {
            setCustomerEmailTouched(true);
            setError(t.errors.invalidEmail);
            return;
        }

        if (verificationMethod === VerificationMethod.PHONE && !customerPhone.trim()) {
            setError(t.errors.needPhoneForVerification);
            return;
        }

        const chosen = slots.find((s) => s.start === slotStart);
        if (!chosen) {
            setError(t.errors.chooseValidTime);
            return;
        }

        try {
            setUi("submitting");
            setError("");
            setSuccessMsg("");

            const res = await createReservation(apiBase, companySlug, {
                serviceId,
                staffId,
                startTime: chosen.start,
                customerName,
                customerEmail,
                customerPhone: customerPhone.trim(),
                notes: notes.trim() || "",
                verificationMethod,
                captchaToken,
                ...(discountCodeFromUrl && discountPercent != null ? { discountCode: discountCodeFromUrl } : {}),
                ...(whatsappEnabled && whatsappOptIn ? { whatsappOptIn: true } : {}),
            });

            if (res.depositCheckoutUrl) {
                // Aanbetaling vereist — doorsturen naar Mollie's checkout i.p.v. hier meteen
                // "gelukt" te tonen. Betalen geldt als bevestiging (zie DepositService/
                // MollieWebhookController), de klant komt na afronden terug op
                // payment-return.html.
                window.location.href = res.depositCheckoutUrl;
                return;
            }

            setUi("success");
            setSuccessMsg(t.success.message(res.id));
        } catch (e: any) {
            setUi("idle");

            // Het gekozen tijdstip bleek tussen het ophalen van de beschikbaarheid en het
            // boeken alsnog bezet of te dichtbij (bv. de klant nam de tijd om het
            // formulier in te vullen) — nooit de rauwe serverfout tonen, gewoon een
            // vriendelijke melding en de beschikbaarheid verversen zodat de klant meteen
            // een nieuwe keuze kan maken.
            if (e?.status === 409) {
                setError(t.errors.slotNoLongerAvailable);
                setSlotStart("");
                setStep(3);
                setSlotsRefreshToken((n) => n + 1);
                return;
            }

            setError(t.errors.bookingFailed);
        }
    }

    // Bewust géén slotsLoading hierin — anders blokkeert elke nieuwe datum-fetch de hele
    // kalender/navigatie even, wat bij snel doorklikken als flikkeren aanvoelt. Alleen de
    // tijd-knoppen zelf (bk-time-item) krijgen daarvoor hun eigen, kleinere disabled-check.
    const disableForm = ui === "loadingInit" || ui === "loadingStaff" || ui === "submitting" || ui === "success";

    if (bookingsClosed) {
        return (
            <div className={`bk-flow ${onDone ? "bk-flow-popup" : "bk-flow-inline"}`}>
                <div className="bk-alert bk-alert-error">{t.errors.bookingsClosed}</div>
            </div>
        );
    }

    return (
        <div className={`bk-flow ${onDone ? "bk-flow-popup" : "bk-flow-inline"}`}>
            {error && <div className="bk-alert bk-alert-error">{error}</div>}

            {ui === "success" ? (
                <div className="bk-success-screen">
                    <div className="bk-success-card">
                        <div className="bk-kicker">{t.success.kicker}</div>
                        <h1 className="bk-page-title">{t.success.title}</h1>
                        <p className="bk-page-copy">{successMsg}</p>

                        <div className="bk-summary-card">
                            {hasMultipleLocations && (
                                <div className="bk-summary-row">
                                    <span>{t.details.summaryLocation}</span>
                                    <strong>{selectedLocation?.name ?? "-"}</strong>
                                </div>
                            )}
                            <div className="bk-summary-row">
                                <span>{t.details.summaryService}</span>
                                <strong>{displayServiceName(selectedService, lang) || "-"}</strong>
                            </div>
                            <div className="bk-summary-row">
                                <span>{t.details.summaryStaff}</span>
                                <strong>{selectedStaff?.name ?? "-"}</strong>
                            </div>
                            <div className="bk-summary-row">
                                <span>{t.details.summaryDate}</span>
                                <strong>{formatDateLabel(date, lang)}</strong>
                            </div>
                            <div className="bk-summary-row">
                                <span>{t.details.summaryTime}</span>
                                <strong>{selectedSlot ? formatTime(selectedSlot.start, lang) : "-"}</strong>
                            </div>
                        </div>

                        <div className="bk-success-actions">
                            <button className="bk-btn bk-btn-ghost" onClick={resetForm} type="button">
                                {t.success.newBooking}
                            </button>
                            <button className="bk-btn bk-btn-primary" onClick={onDone} type="button">
                                {t.success.close}
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="bk-step-topbar">
                        {/* Compacte genummerde stepper i.p.v. platte "Stap X van Y"-tekst —
                            elke bol toont de voortgang in één oogopslag; voltooide stappen
                            krijgen een vinkje, de huidige stap is gevuld met de merkkleur. De
                            titel van elke stap staat al in de eigen <h1> hieronder, dus geen
                            aparte labels nodig (zou bij 5 stappen niet passen op smalle breedtes). */}
                        <ol className="bk-stepper" aria-label={t.stepLabel(step, totalSteps)}>
                            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((n) => (
                                <li className="bk-stepper-item" key={n}>
                                    <span
                                        className={`bk-stepper-dot${n < step ? " is-done" : n === step ? " is-active" : ""}`}
                                        aria-current={n === step ? "step" : undefined}
                                    >
                                        {n < step ? "✓" : n}
                                    </span>
                                    {n < totalSteps && (
                                        <span className={`bk-stepper-line${n < step ? " is-done" : ""}`} />
                                    )}
                                </li>
                            ))}
                        </ol>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            {multiLangEnabled && (
                                <div className="bk-lang-toggle" role="group" aria-label={t.langToggleAria}>
                                    <button
                                        type="button"
                                        className={`bk-lang-btn bk-lang-btn-flag ${lang === "nl" ? "is-active" : ""}`}
                                        onClick={() => changeLang("nl")}
                                        aria-label="Nederlands"
                                        aria-pressed={lang === "nl"}
                                        title="Nederlands"
                                    >
                                        <FlagNL />
                                    </button>
                                    <button
                                        type="button"
                                        className={`bk-lang-btn bk-lang-btn-flag ${lang === "en" ? "is-active" : ""}`}
                                        onClick={() => changeLang("en")}
                                        aria-label="English"
                                        aria-pressed={lang === "en"}
                                        title="English"
                                    >
                                        <FlagGB />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="bk-step-body">
                        {stepKey(step) === "location" && (
                            <section className="bk-step-section">
                                <header className="bk-page-header">
                                    <h1 className="bk-page-title">{t.location.title}</h1>
                                    <p className="bk-page-copy">{t.location.copy}</p>
                                </header>

                                <div className="bk-service-list">
                                    {locations.map((loc) => {
                                        const selected = loc.id === locationId;
                                        const addressLine = [loc.street, loc.houseNumber].filter(Boolean).join(" ");
                                        const cityLine = [loc.postalCode, loc.city].filter(Boolean).join(" ");

                                        return (
                                            <button
                                                key={loc.id}
                                                type="button"
                                                className={`bk-service-item ${selected ? "is-selected" : ""}`}
                                                onClick={() => {
                                                    setLocationId(loc.id);
                                                    setError("");
                                                }}
                                                disabled={disableForm}
                                            >
                                                <div className="bk-service-top">
                                                    <div className="bk-service-title-wrap">
                                                        <h3 className="bk-service-title">{loc.name}</h3>
                                                        {selected && <span className="bk-chip">{t.location.selected}</span>}
                                                    </div>
                                                    {!selected && <span className="bk-service-arrow">›</span>}
                                                </div>

                                                {(addressLine || cityLine) && (
                                                    <div className="bk-service-meta">
                                                        <span>{[addressLine, cityLine].filter(Boolean).join(", ")}</span>
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}

                                    {!locations.length && ui === "loadingInit" && (
                                        <div className="bk-empty-state">{t.location.loading}</div>
                                    )}
                                </div>
                            </section>
                        )}

                        {stepKey(step) === "service" && (
                            <section className="bk-step-section">
                                <header className="bk-page-header">
                                    <h1 className="bk-page-title">{t.service.title}</h1>
                                    <p className="bk-page-copy">{t.service.copy}</p>
                                </header>

                                {hasServiceCategories && (
                                    <div className="bk-category-bar">
                                        <button
                                            type="button"
                                            className={`bk-category-pill ${selectedServiceCategory === "all" ? "is-active" : ""}`}
                                            onClick={() => setSelectedServiceCategory("all")}
                                        >
                                            {t.service.allCategories}
                                        </button>
                                        {serviceCategoryTabs.map((cat) => (
                                            <button
                                                key={cat.id ?? "other"}
                                                type="button"
                                                className={`bk-category-pill ${selectedServiceCategory === cat.id ? "is-active" : ""}`}
                                                onClick={() => setSelectedServiceCategory(cat.id)}
                                            >
                                                {cat.name}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {(hasServiceCategories || services.length > 6) && (
                                    <div className="bk-service-search">
                                        <span className="bk-service-search-icon" aria-hidden="true">⌕</span>
                                        <input
                                            type="text"
                                            value={serviceSearch}
                                            onChange={(e) => setServiceSearch(e.target.value)}
                                            placeholder={t.service.searchPlaceholder}
                                        />
                                    </div>
                                )}

                                <div className="bk-service-list">
                                    {visibleServices.map((s: any) => {
                                        const selected = s.id === serviceId;
                                        const price = formatPrice(s.priceCents, lang);
                                        const duration = s.durationMinutes ?? s.durationMin;

                                        return (
                                            <button
                                                key={s.id}
                                                type="button"
                                                className={`bk-service-item ${selected ? "is-selected" : ""}`}
                                                onClick={() => {
                                                    setServiceId(s.id);
                                                    setError("");
                                                }}
                                                disabled={disableForm}
                                            >
                                                <div className="bk-service-top">
                                                    <div className="bk-service-title-wrap">
                                                        <h3 className="bk-service-title">{displayServiceName(s, lang)}</h3>
                                                        {selected && <span className="bk-chip">{t.service.selected}</span>}
                                                        {!selected && s.capacity > 1 && (
                                                            <span className="bk-chip bk-chip-muted">{t.service.groupBadge}</span>
                                                        )}
                                                    </div>
                                                    {!selected && <span className="bk-service-arrow">›</span>}
                                                </div>

                                                <div className="bk-service-meta">
                                                    {duration ? (
                                                        <span>
                                                            {duration} {t.service.minSuffix}
                                                        </span>
                                                    ) : (
                                                        <span>—</span>
                                                    )}
                                                    {price ? <strong>{price}</strong> : null}
                                                </div>
                                            </button>
                                        );
                                    })}

                                    {!services.length && ui === "loadingInit" && (
                                        <div className="bk-empty-state">{t.service.loading}</div>
                                    )}

                                    {services.length > 0 && visibleServices.length === 0 && (
                                        <div className="bk-empty-state">{t.service.noResults}</div>
                                    )}
                                </div>

                                <div className="bk-info-panel">
                                    <span className="bk-info-icon">i</span>
                                    <p>{t.service.infoPanel}</p>
                                </div>
                            </section>
                        )}

                        {stepKey(step) === "staff" && (
                            <section className="bk-step-section">
                                <header className="bk-page-header">
                                    <h1 className="bk-page-title">{t.staff.title}</h1>
                                    <p className="bk-page-copy">{t.staff.copy}</p>
                                </header>

                                <div className="bk-staff-grid">
                                    {staff.map((st: any, idx) => {
                                        const selected = st.id === staffId;
                                        const subtitle = st.roleLabel || st.role || t.staff.roleFallbacks[idx % 4];

                                        return (
                                            <button
                                                key={st.id}
                                                type="button"
                                                className={`bk-staff-card ${selected ? "is-selected" : ""}`}
                                                onClick={() => {
                                                    setStaffId(st.id);
                                                    setError("");
                                                }}
                                                disabled={disableForm}
                                            >
                                                <div className="bk-staff-card-head">
                                                    <span className={`bk-badge ${selected ? "is-active" : ""}`}>
                                                        {selected ? t.staff.badgeSelected : t.staff.badgeAvailable}
                                                    </span>
                                                    <div className="bk-avatar">{getInitials(st.name)}</div>
                                                </div>

                                                <div className="bk-staff-content">
                                                    <h3>{st.name}</h3>
                                                    <p>{subtitle}</p>
                                                </div>

                                                <div className="bk-staff-link">
                                                    {selected ? t.staff.linkChange : t.staff.linkSelect}
                                                </div>
                                            </button>
                                        );
                                    })}

                                    {!staff.length && ui === "loadingStaff" && (
                                        <div className="bk-empty-state">{t.staff.loading}</div>
                                    )}
                                </div>
                            </section>
                        )}

                        {stepKey(step) === "dateTime" && (
                            <section className="bk-step-section bk-datetime-step">
                                <div className="bk-datetime-sidebar">
                                    <div className="bk-kicker">{t.dateTime.kicker}</div>
                                    <h1 className="bk-page-title">{t.dateTime.title}</h1>
                                    <p className="bk-page-copy">{t.dateTime.copy}</p>

                                    <div className="bk-context-card">
                                        <div className="bk-context-row">
                                            <span>🗓</span>
                                            <span>{formatMonthLabel(date, lang)}</span>
                                        </div>
                                        <div className="bk-context-row">
                                            <span>◷</span>
                                            <span>
                                                {selectedService?.durationMinutes ?? (selectedService as any)?.durationMin ?? "-"}{" "}
                                                {t.dateTime.minutesSuffix}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="bk-datetime-main">
                                    <div className="bk-calendar-card">
                                        <div className="bk-calendar-head">
                                            <button
                                                type="button"
                                                className="bk-calendar-nav"
                                                aria-label={t.dateTime.prevMonthAria}
                                                onClick={() => shiftViewMonth(-1)}
                                                disabled={isCurrentMonth || disableForm}
                                            >
                                                ‹
                                            </button>
                                            <h2>{formatMonthLabel(`${viewMonth}-01`, lang)}</h2>
                                            <button
                                                type="button"
                                                className="bk-calendar-nav"
                                                aria-label={t.dateTime.nextMonthAria}
                                                onClick={() => shiftViewMonth(1)}
                                                disabled={disableForm}
                                            >
                                                ›
                                            </button>
                                        </div>

                                        <div className="bk-calendar-weekdays">
                                            {t.dateTime.weekdays.map((wd) => (
                                                <span key={wd}>{wd}</span>
                                            ))}
                                        </div>

                                        <div className="bk-calendar-grid">
                                            {calendarDays.map((day) => {
                                                const selected = day.iso === date;

                                                return (
                                                    <button
                                                        key={day.iso}
                                                        type="button"
                                                        className={[
                                                            "bk-calendar-day",
                                                            !day.inMonth ? "is-outside" : "",
                                                            selected ? "is-selected" : "",
                                                            day.isPast ? "is-disabled" : "",
                                                        ]
                                                            .filter(Boolean)
                                                            .join(" ")}
                                                        disabled={day.isPast || disableForm}
                                                        onClick={() => {
                                                            setDate(day.iso);
                                                            if (!day.inMonth) {
                                                                setViewMonth(day.iso.slice(0, 7));
                                                            }
                                                            setError("");
                                                        }}
                                                    >
                                                        {day.day}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="bk-time-card">
                                        <h2>{t.dateTime.availableTimesTitle}</h2>
                                        <div
                                            className="bk-time-list"
                                            style={{
                                                opacity: slotsLoading ? 0.55 : 1,
                                                transition: "opacity 150ms ease",
                                            }}
                                        >
                                            {slots.map((s) => {
                                                const selected = s.start === slotStart;
                                                const isGroup = !!s.capacity && s.capacity > 1;
                                                const spotsLeft = isGroup ? s.capacity! - (s.booked ?? 0) : null;

                                                return (
                                                    <button
                                                        key={s.start}
                                                        type="button"
                                                        className={`bk-time-item ${selected ? "is-selected" : ""}`}
                                                        onClick={() => {
                                                            setSlotStart(s.start);
                                                            setError("");
                                                        }}
                                                        disabled={disableForm || slotsLoading}
                                                    >
                                                        <span>
                                                            {formatTime(s.start, lang)}
                                                            {isGroup && (
                                                                <span className="bk-time-item-spots">
                                                                    {t.dateTime.spotsLeft(spotsLeft!)}
                                                                </span>
                                                            )}
                                                        </span>
                                                        <span>{selected ? "✓" : ""}</span>
                                                    </button>
                                                );
                                            })}

                                            {!slots.length && slotsLoading && (
                                                <div className="bk-empty-state">{t.dateTime.loadingSlots}</div>
                                            )}

                                            {!slots.length && !slotsLoading && canLoadSlots && (
                                                <div className="bk-empty-state">{t.dateTime.noSlots}</div>
                                            )}

                                            {!slots.length && !slotsLoading && canLoadSlots && waitlistEnabled && serviceId && (
                                                <WaitlistForm
                                                    key={`${serviceId}-${staffId}-${date}`}
                                                    t={t}
                                                    apiBase={apiBase}
                                                    companySlug={companySlug}
                                                    serviceId={serviceId}
                                                    staffId={staffId}
                                                    date={date}
                                                    initialName={customerName}
                                                    initialEmail={customerEmail}
                                                    initialPhone={customerPhone}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </section>
                        )}

                        {stepKey(step) === "details" && (
                            <section className="bk-step-section">
                                <div className="bk-details-layout">
                                    <div>
                                        <header className="bk-page-header">
                                            <h1 className="bk-page-title">{t.details.title}</h1>
                                            <p className="bk-page-copy">{t.details.copy}</p>
                                        </header>

                                        <div className="bk-form-grid">
                                            <label className="bk-field">
                                                <span>{t.details.nameLabel}</span>
                                                <input
                                                    className="bk-input"
                                                    value={customerName}
                                                    onChange={(e) => setCustomerName(e.target.value)}
                                                    placeholder={t.details.namePlaceholder}
                                                    disabled={disableForm}
                                                />
                                            </label>

                                            <label className="bk-field">
                                                <span>{t.details.emailLabel}</span>
                                                <input
                                                    type="email"
                                                    className={`bk-input${showEmailError ? " is-invalid" : ""}`}
                                                    value={customerEmail}
                                                    onChange={(e) => setCustomerEmail(e.target.value)}
                                                    onBlur={() => setCustomerEmailTouched(true)}
                                                    placeholder={t.details.emailPlaceholder}
                                                    disabled={disableForm}
                                                    aria-invalid={showEmailError}
                                                />
                                                {showEmailError && <p className="bk-field-error">{t.errors.invalidEmail}</p>}
                                            </label>

                                            <label className="bk-field">
                                                <span>{t.details.phoneLabel}</span>
                                                <input
                                                    className="bk-input"
                                                    value={customerPhone}
                                                    onChange={(e) => setCustomerPhone(e.target.value)}
                                                    placeholder={t.details.phonePlaceholder}
                                                    disabled={disableForm}
                                                />
                                            </label>

                                            <label className="bk-field">
                                                <span>{t.details.verificationLabel}</span>
                                                <select
                                                    className="bk-input bk-select"
                                                    value={verificationMethod}
                                                    onChange={(e) => setVerificationMethod(e.target.value as any)}
                                                    disabled={disableForm}
                                                >
                                                    <option value={VerificationMethod.EMAIL}>{t.details.verificationEmail}</option>
                                                    {smsEnabled && (
                                                        <option value={VerificationMethod.PHONE}>{t.details.verificationPhone}</option>
                                                    )}
                                                </select>
                                            </label>

                                            <label className="bk-field bk-field-full">
                                                <span>{t.details.notesLabel}</span>
                                                <textarea
                                                    className="bk-input bk-textarea"
                                                    value={notes}
                                                    onChange={(e) => setNotes(e.target.value)}
                                                    placeholder={t.details.notesPlaceholder}
                                                    rows={4}
                                                    disabled={disableForm}
                                                />
                                            </label>

                                            {whatsappEnabled && (
                                                <label className="bk-field-full bk-checkbox-field">
                                                    <input
                                                        type="checkbox"
                                                        checked={whatsappOptIn}
                                                        onChange={(e) => setWhatsappOptIn(e.target.checked)}
                                                        disabled={disableForm}
                                                    />
                                                    <span className="bk-checkbox-field-text">
                                                        <span className="bk-checkbox-field-label">
                                                            {t.details.whatsappOptInLabel}
                                                        </span>
                                                        <span className="bk-checkbox-field-hint">
                                                            {t.details.whatsappOptInHint}
                                                        </span>
                                                    </span>
                                                </label>
                                            )}
                                        </div>
                                    </div>

                                    <aside className="bk-summary-card">
                                        <div className="bk-summary-title">{t.details.summaryTitle}</div>

                                        {hasMultipleLocations && (
                                            <div className="bk-summary-row">
                                                <span>{t.details.summaryLocation}</span>
                                                <strong>{selectedLocation?.name ?? "-"}</strong>
                                            </div>
                                        )}
                                        <div className="bk-summary-row">
                                            <span>{t.details.summaryService}</span>
                                            <strong>{displayServiceName(selectedService, lang) || "-"}</strong>
                                        </div>
                                        <div className="bk-summary-row">
                                            <span>{t.details.summaryStaff}</span>
                                            <strong>{selectedStaff?.name ?? "-"}</strong>
                                        </div>
                                        <div className="bk-summary-row">
                                            <span>{t.details.summaryDate}</span>
                                            <strong>{formatDateLabel(date, lang)}</strong>
                                        </div>
                                        <div className="bk-summary-row">
                                            <span>{t.details.summaryTime}</span>
                                            <strong>{selectedSlot ? formatTime(selectedSlot.start, lang) : "-"}</strong>
                                        </div>
                                        <div className="bk-summary-row">
                                            <span>{t.details.summaryPrice}</span>
                                            {discountPercent != null && typeof (selectedService as any)?.priceCents === "number" ? (
                                                <strong>
                                                    <span className="bk-summary-price-original">
                                                        {formatPrice((selectedService as any).priceCents, lang)}
                                                    </span>
                                                    <span className="bk-summary-price-discounted">
                                                        {formatPrice(
                                                            Math.round((selectedService as any).priceCents * (1 - discountPercent / 100)),
                                                            lang
                                                        )}
                                                    </span>
                                                </strong>
                                            ) : (
                                                <strong>{formatPrice((selectedService as any)?.priceCents, lang) ?? "-"}</strong>
                                            )}
                                        </div>
                                        {discountPercent != null && (
                                            <p className="bk-summary-discount-tag">
                                                {t.details.summaryDiscountApplied} (-{discountPercent}%)
                                            </p>
                                        )}
                                        {depositEnabled && depositAmountCents > 0 && (
                                            <>
                                                <div className="bk-summary-row">
                                                    <span>{t.details.summaryDeposit}</span>
                                                    <strong>{formatPrice(depositAmountCents, lang)}</strong>
                                                </div>
                                                <p className="bk-summary-deposit-notice">{t.details.depositNotice}</p>
                                            </>
                                        )}
                                    </aside>
                                </div>
                            </section>
                        )}
                    </div>

                    <footer className="bk-bottom-nav">
                        <button
                            className="bk-btn bk-btn-secondary"
                            type="button"
                            onClick={prevStep}
                            disabled={step === 1 || disableForm}
                        >
                            {t.nav.prev}
                        </button>

                        {step < totalSteps ? (
                            <button
                                className="bk-btn bk-btn-primary"
                                type="button"
                                onClick={nextStep}
                                disabled={disableForm}
                            >
                                {t.nav.next}
                            </button>
                        ) : (
                            <button
                                className="bk-btn bk-btn-primary"
                                onClick={onSubmit}
                                disabled={disableForm}
                                type="button"
                            >
                                {ui === "submitting" ? t.nav.submitting : t.nav.submit}
                            </button>
                        )}
                    </footer>
                </>
            )}

            {ui === "submitting" && (
                <div className="bk-processing">
                    <div className="bk-processing-card">
                        <div className="bk-processing-title">{t.processing.title}</div>
                        <div className="bk-processing-copy">{t.processing.copy}</div>
                        <div className="bk-progress">
                            <div />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
